/**
 * This app's live-data declarations (docs/LIVE-DATA.md): which screens are
 * status views, and which queries show each kind of record. The machinery is
 * in src/lib/live-data.ts; this file is the part a project adds to as it
 * grows.
 */
import { orpc } from '@/adapters/rpc/client'
import type { TouchMap } from '@/lib/live-data'

export interface StatusView {
  /** Where a person sees it. */
  screen: string
  /** The exact code that reads it — the test finds it and checks its refresh. */
  reads: string
  /** Longest allowed refresh, if it must be above 30 s — with the reason. */
  slower?: { everyMs: number; because: string }
}

/**
 * Every status view: it shows something that changes on its own (someone
 * else's sign-in, a background job), so it polls while visible.
 * `live-data.test.ts` fails if any of these reads has no refetch interval, or
 * a slower one than allowed.
 */
export const STATUS_VIEWS: readonly StatusView[] = [
  {
    screen: 'Settings → Users: Last active',
    reads: 'orpc.users.list.queryOptions(),',
    slower: { everyMs: 60_000, because: '"Last active" reads in minutes' },
  },
  {
    screen: 'Request sent (the waiting room): approval',
    reads: 'meQueryOptions(), 30_000',
  },
  {
    screen: 'Sandbox banner: changes waiting for production',
    reads: "queryKey: ['build', 'waiting']",
    slower: { everyMs: 60_000, because: 'the server caches production’s answer for a minute' },
  },
  {
    // While open: how the dialog sees Railway's restart land (D-019).
    screen: 'System → Reconnect: which deployment is answering',
    reads: 'orpc.system.reconnectInfo.queryOptions(',
  },
  {
    screen: 'Settings → MCP access: the usage log',
    reads: 'orpc.mcp.admin.usage.queryOptions(',
  },
  {
    screen: 'System → Change: videos',
    reads: "queryKey: ['changes', 'videos']",
    slower: { everyMs: 60_000, because: 'the server reads the bucket at most once a minute' },
  },
  {
    screen: 'Settings → Webhooks: health, last received',
    reads: 'statusQuery(orpc.webhooks.list.queryOptions(), EVERY)',
  },
  {
    screen: 'A webhook: health, no-data alert status',
    reads: 'statusQuery(orpc.webhooks.get.queryOptions({ input: { id } }), EVERY)',
  },
  {
    screen: 'A webhook: run history',
    reads: '...orpc.webhooks.deliveries.queryOptions({ input: { id, page, pageSize } }),',
  },
  {
    screen: 'A webhook: flag history',
    reads: '...orpc.webhooks.alertFlags.queryOptions({ input: { id, page, pageSize } }),',
  },
  {
    // Polls fast only while an import runs (the page raises it to 3 s then).
    screen: 'Settings → Import from Monday: the import in progress',
    reads: 'statusQuery(orpc.import.status.queryOptions({ input: {} }), IDLE)',
  },
  {
    screen: 'Webhooks: Recently deleted',
    reads: 'statusQuery(orpc.webhooks.listDeleted.queryOptions(), EVERY)',
  },
  {
    screen: 'Admin banner: webhooks needing attention',
    reads: 'statusQuery(orpc.webhooks.alarms.queryOptions(), 60_000)',
    slower: {
      everyMs: 60_000,
      because:
        'each read judges every webhook (28 days of hourly counts apiece) and the words are minute-grained — “nothing since 2:10 PM”',
    },
  },
]

/** The kinds of record a mutation can touch. */
export type RecordKind =
  | 'items'
  | 'people'
  | 'autoJoin'
  | 'me'
  | 'system'
  | 'mcp'
  | 'dictionary'
  | 'webhooks'
  | 'jobs'
  | 'clients'
  | 'cleaners'
  | 'invoices'
  | 'messages'
  | 'business'
  | 'orders'
  | 'inspections'
  | 'import'

/**
 * The touch map: every query that shows each kind. Add a screen's key here
 * and every mutation that touches that kind refreshes it.
 */
export const TOUCHES: TouchMap<RecordKind> = {
  // An item's record history changes with it.
  items: () => [orpc.items.key(), orpc.recordHistory.key()],
  // Names also show beside items (who made them) and in the assistant's budget.
  people: () => [orpc.users.list.key(), orpc.items.key(), orpc.system.assistantUsage.key()],
  autoJoin: () => [orpc.users.autoJoin.key()],
  // Your own name appears in the sidebar, the users list and on records.
  me: () => [orpc.me.key(), ...TOUCHES.people()],
  // Prices, meters and the checks "Connected" comes from.
  system: () => [orpc.system.key(), orpc.setup.key()],
  // MCP access: the switch, tokens, connections and the usage log share one namespace.
  mcp: () => [orpc.mcp.key()],
  // Every label on every screen reads the dictionary (docs/DATA-MODEL.md).
  dictionary: () => [orpc.dictionary.key()],
  // A webhook, its runs, its alert and flags, the Admin banner — and System
  // → Setup's Data health check, which reads the same verdict.
  webhooks: () => [orpc.webhooks.key(), orpc.setup.key()],
  // A job, its page, its Activity log and the counts above the list — and
  // search, which finds jobs by name. A new job's first notice, and a message
  // about a job, land in Messages.
  jobs: () => [
    orpc.jobs.key(),
    orpc.messages.key(),
    orpc.today.key(),
    orpc.schedule.key(),
    orpc.recurring.key(),
    orpc.clients.key(),
    orpc.cleaners.key(),
    orpc.invoices.key(),
    orpc.search.key(),
  ],
  // A client, its locations, people, monthly items, messages and notes — and the jobs,
  // invoices and conversations that name them.
  clients: () => [
    orpc.clients.key(),
    orpc.billing.key(),
    orpc.people.key(),
    orpc.messages.key(),
    orpc.invoices.key(),
    orpc.today.key(),
    orpc.schedule.key(),
    orpc.contacts.key(),
    orpc.jobs.key(),
    orpc.orders.key(),
    orpc.inspections.key(),
    orpc.search.key(),
  ],
  // A cleaner, their week and messages — and everything that names them: jobs,
  // clients, the Schedule, recurring jobs, Messages and the contacts behind them.
  cleaners: () => [
    orpc.cleaners.key(),
    orpc.jobs.key(),
    orpc.clients.key(),
    orpc.today.key(),
    orpc.schedule.key(),
    orpc.recurring.key(),
    orpc.messages.key(),
    orpc.contacts.key(),
    orpc.people.key(),
    orpc.search.key(),
  ],
  // An invoice, its page and Activity — and the jobs it bills, its client's
  // Invoices tab, search, and Messages (sending it emails the billing contact).
  invoices: () => [
    orpc.invoices.key(),
    orpc.messages.key(),
    orpc.jobs.key(),
    orpc.clients.key(),
    orpc.today.key(),
    orpc.search.key(),
  ],
  // The Monday import's run: its files, preview and result.
  import: () => [orpc.import.key()],
  // A conversation, the inbox and its unread count — and the records whose Messages and Activity show it.
  messages: () => [
    orpc.messages.key(),
    orpc.clients.key(),
    orpc.cleaners.key(),
    orpc.jobs.key(),
    orpc.today.key(),
  ],
  // The business rules are read by every Majik screen: notices, invoices, Today.
  business: () => [
    orpc.businessSettings.key(),
    orpc.jobs.key(),
    orpc.invoices.key(),
    orpc.today.key(),
    orpc.cleaners.key(),
    // A location's "we miss you" note reads the reminder days.
    orpc.clients.key(),
    // The low-stock mark and the time zone.
    orpc.orders.key(),
    orpc.schedule.key(),
    orpc.messages.key(),
    orpc.inspections.key(),
  ],
  // An order, its products' stock, and Today.
  orders: () => [orpc.orders.key(), orpc.today.key()],
  // An inspection, and the inspector's messages.
  inspections: () => [orpc.inspections.key(), orpc.messages.key(), orpc.today.key()],
}
